import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import Badge from '@orn/shared-web/components/Badge/Badge';
import { isValidDateOfBirth } from '@orn/shared-web/components/DateOfBirthInput';
import { useBreakpoint } from '@orn/shared-web/hooks/useBreakpoint';
import logger from '@orn/shared-web/lib/logger';
import * as React from 'react';

import { useDeleteResidence } from '../../../hooks/mutations/useDeleteResidence';
import { useStartDependentPhoneVerification } from '../../../hooks/mutations/useStartDependentPhoneVerification';
import { useUpdateCaregiverRelationship } from '../../../hooks/mutations/useUpdateCaregiverRelationship';
import { useUpdateDependent } from '../../../hooks/mutations/useUpdateDependent';
import { useUpdateDependentContact } from '../../../hooks/mutations/useUpdateDependentContact';
import { useUpdateDependentEmail } from '../../../hooks/mutations/useUpdateDependentEmail';
import { useUpdateDependentPartnership } from '../../../hooks/mutations/useUpdateDependentPartnership';
import { useUpdateDependentPhoneNumber } from '../../../hooks/mutations/useUpdateDependentPhoneNumber';
import { useUpsertResidence } from '../../../hooks/mutations/useUpsertResidence';
import { useDependentResidences } from '../../../hooks/queries/useDependentResidences';
import { useDependents } from '../../../hooks/queries/useDependents';
import useAutoCommit from '../../../hooks/useAutoCommit';
import { useFeatureGate } from '../../../hooks/useFeatureGates';
import { displayName, formatRelationship } from '../../../lib/dependents/helpers';
import { isCompleteAddress } from '../../../lib/dependents/residenceAddress';
import { getOptionalEnv } from '../../../lib/getEnv';
import { CollapsibleSettingsCard, SettingsTabLoading } from '../common';

import DependentCardBody from './DependentCardBody';
import {
  buildCardInputs,
  emailFieldError,
  emailRefusalMessage,
  hasEmailChanged,
  hasPhoneChanged,
  phoneRefusalMessage,
  isCardValid,
  isMidEditFrequentPlace,
  residenceFieldErrors,
  seedCardState,
} from './dependentCardModel';
import { applyFrozenOrder, orderDependents } from './orderDependents';
import {
  buildFrequentPlaceInputs,
  buildResidenceInputs,
  hydrateFrequentLocations,
  INITIAL_FREQUENT_LOCATIONS_STATE,
  type IFrequentLocationsState,
  type IFrequentPlace,
} from './residenceForm';
import StartPhoneVerificationModal from './StartPhoneVerificationModal';
import TabbedDependentsCard from './TabbedDependentsCard';

import type { IDependentCardBodyProps } from './DependentCardBody';
import type { IDependentCardState, ResidenceMode } from './dependentCardModel';
import type { IDependentRow } from './orderDependents';
import type { UpsertResidenceInput } from '../../../__generated__/globalTypes';

/** Drops one card's refusal, keeping the record as-is when that card has none. */
const withoutCard =
  (id: string) =>
  (prev: Record<string, string>): Record<string, string> => {
    if (prev[id] === undefined) {
      return prev;
    }
    const next = { ...prev };
    delete next[id];
    return next;
  };

/** Main vs Referential for a row: the partner card is Referential only while the live "Lives with {first}" answer is yes. */
const modeForRow = (row: IDependentRow, liveTogether: boolean | null): ResidenceMode =>
  row.isPartnerCard && liveTogether === true ? 'referential' : 'main';

// ---------------------------------------------------------------------------
// Card (presentational) — one collapsible dependent card.
// ---------------------------------------------------------------------------

export interface IDependentCardProps extends IDependentCardBodyProps {
  isExpanded: boolean;
  onToggle: () => void;
}

/**
 * One collapsible dependent card: a header with the dependent's name + a
 * relationship badge, and (when expanded) the editable sections from
 * `DependentCardBody`. Fully controlled — all state + callbacks come from the
 * form wrapper.
 */
const DependentCard = ({ isExpanded, onToggle, ...body }: IDependentCardProps) => {
  const name = displayName({ firstName: body.state.firstName, preferredName: body.state.preferredName }) || 'Loved one';

  return (
    <CollapsibleSettingsCard
      isExpanded={isExpanded}
      onToggle={onToggle}
      toggleLabel={name}
      header={
        <div className="flex items-center gap-2">
          <span className="text-primary text-lg font-semibold">{name}</span>
          {body.state.relationship && (
            <Badge type="pill-color" size="sm" color="brand">
              {formatRelationship({
                relationship: body.state.relationship,
                isStep: body.state.isStep,
                isInLaw: body.state.isInLaw,
              })}
            </Badge>
          )}
        </div>
      }
    >
      <DependentCardBody {...body} />
    </CollapsibleSettingsCard>
  );
};

DependentCard.displayName = 'DependentCard';

// ---------------------------------------------------------------------------
// Base (presentational) — the dependent cards, accordion or tabbed by viewport.
// ---------------------------------------------------------------------------

/** The confirmation step, when one is open. Null keeps the modal unmounted. */
export interface IPhoneVerificationPrompt {
  name: string;
  phoneNumber: string;
  isStarting: boolean;
  error?: string;
  onClose: () => void;
  onConfirm: () => void;
}

export interface IDependentSettingsFormBaseProps {
  cards: IDependentCardProps[];
  saveError?: string;
  verification?: IPhoneVerificationPrompt | null;
}

/**
 * Presentational dependents settings form. On `lg+` viewports it renders a
 * stack of collapsible dependent cards; below `lg` (mobile/tablet) it renders
 * one `TabbedDependentsCard` with a person tab per dependent. In both layouts
 * each dependent's sections persist their own edits via `onCommit` — there is
 * no save button. Holds no data or mutations — the `DependentSettingsForm`
 * wrapper owns state, ordering, and persistence.
 */
export const DependentSettingsFormBase = ({ cards, saveError, verification }: IDependentSettingsFormBaseProps) => {
  const isDesktop = useBreakpoint('lg');

  return (
    // [overflow-anchor:none]: see CollapsibleSettingsCard — the cards' sibling
    // (the error line) must not become a scroll anchor either.
    <div className="flex max-w-[736px] flex-col gap-5 [overflow-anchor:none]">
      {isDesktop ? (
        cards.map((card) => <DependentCard key={card.dependentId} {...card} />)
      ) : (
        <TabbedDependentsCard people={cards} />
      )}
      {saveError && <span className="text-error-primary text-sm sm:self-end">{saveError}</span>}
      {verification && (
        <StartPhoneVerificationModal
          isOpen
          name={verification.name}
          phoneNumber={verification.phoneNumber}
          isStarting={verification.isStarting}
          error={verification.error}
          onClose={verification.onClose}
          onConfirm={verification.onConfirm}
        />
      )}
    </div>
  );
};

DependentSettingsFormBase.displayName = 'DependentSettingsFormBase';

// ---------------------------------------------------------------------------
// Wrapper (data) — fetches dependents/residences/caregiver address, owns edit
// state (per-card + the shared liveTogether + frequent-locations list), and on
// each commit fans the diff out to the dependent / relationship / contact /
// residence / partnership mutations, computing residence deletes once across
// the household.
// ---------------------------------------------------------------------------

/**
 * "Loved ones" settings tab. Fetches the caregiver's dependents, their
 * residences, and the caregiver's own address and orders the cards by living
 * situation. Edits persist as they happen — each commit point (text-input
 * blur, select/checkbox/pick change) fans the per-card diff out to
 * `updateDependant`, `updateCaregiverRelationship`, `updateDependentEmail`,
 * `updateDependentPhoneNumber`, `updateDependant` (a cleared number),
 * `upsertResidence`, `deleteResidence`, and `updateDependantPartnership` via
 * `Promise.allSettled`; a commit is a no-op while any card is invalid.
 * Residence deletes are computed as a single household-wide diff (hydrated ids
 * minus the ids still kept by any upsert) so a partner's or the caregiver's
 * shared residence is never deleted as a side effect.
 */
const DependentSettingsForm = () => {
  const apiKey = getOptionalEnv('VITE_GOOGLE_PLACES_API_KEY') ?? '';

  const { dependents, partnership, loading: dependentsLoading } = useDependents();
  const {
    dependentResidences,
    caregiverPrimaryResidence,
    loading: residencesLoading,
    refetch: refetchResidences,
  } = useDependentResidences();

  const [updateDependent] = useUpdateDependent();
  const [updateCaregiverRelationship] = useUpdateCaregiverRelationship();
  const [updateDependentContact] = useUpdateDependentContact();
  const [upsertResidence] = useUpsertResidence();
  const [deleteResidence] = useDeleteResidence();
  const [updateDependentPartnership] = useUpdateDependentPartnership();
  const [updateDependentEmail] = useUpdateDependentEmail();
  const [updateDependentPhoneNumber] = useUpdateDependentPhoneNumber();
  const [startPhoneVerification, { loading: isStartingVerification }] = useStartDependentPhoneVerification();
  // The same gate the mutation resolves server-side. Withholding the callback
  // rather than disabling the button: an ungated caregiver has nothing to wait
  // for, so a grayed control with a tooltip would describe a capability they
  // cannot be given. `useFeatureGate` fails closed while it loads.
  const canStartVerification = useFeatureGate(FeatureGate.LovedOneInvitations);

  const residencesById = React.useMemo(
    () => new Map(dependentResidences.map((d) => [d.dependentId, d])),
    [dependentResidences]
  );
  const rows = React.useMemo(
    () => orderDependents(dependents, residencesById, partnership),
    [dependents, residencesById, partnership]
  );

  const [cardStates, setCardStates] = React.useState<Record<string, IDependentCardState>>({});
  const [expanded, setExpanded] = React.useState<Record<string, boolean>>({});
  const [liveTogether, setLiveTogether] = React.useState<boolean | null>(null);
  const [frequentLocations, setFrequentLocations] = React.useState<IFrequentLocationsState>(
    INITIAL_FREQUENT_LOCATIONS_STATE
  );
  const baselineRef = React.useRef<Record<string, IDependentCardState>>({});
  const liveTogetherBaselineRef = React.useRef<boolean | null>(null);
  const frequentBaselineRef = React.useRef<IFrequentLocationsState>(INITIAL_FREQUENT_LOCATIONS_STATE);
  // Every hydrated, non-caregiver-owned `Residence.id`. At save we delete the
  // ones no upsert keeps (excludes `ownerKind === 'user'` so the caregiver's own
  // home is never deleted, and only deletes ids we hydrated so a partner's still
  // -kept shared row survives).
  const hydratedResidenceIdsRef = React.useRef<ReadonlySet<string>>(new Set());
  const [saveError, setSaveError] = React.useState<string | null>(null);
  // Server refusals of an email or a number, held per loved one. The commit fires
  // every card's mutations together, so a refusal has to keep its card's id to
  // land under the field it is actually about.
  const [emailErrors, setEmailErrors] = React.useState<Record<string, string>>({});
  const [phoneErrors, setPhoneErrors] = React.useState<Record<string, string>>({});
  // Which loved one's confirmation step is open, and what the last attempt
  // said. Held by id rather than by row so a refetch reordering the roster
  // cannot move the prompt onto somebody else.
  const [verifyingDependentId, setVerifyingDependentId] = React.useState<string | null>(null);
  const [verifyError, setVerifyError] = React.useState<string | null>(null);
  // State (not a ref) on purpose: flipping it back to true after a refetch is
  // what re-runs the seed effect — relying on the refetch's cache broadcast to
  // render is not enough, since a broadcast never comes when the refetched data
  // matches what the mutation responses already wrote.
  const [pendingHydration, setPendingHydration] = React.useState(true);
  const hasEverHydratedRef = React.useRef(false);
  // True from a residence create/delete commit until the post-refetch re-seed
  // lands — the one window where edits would be clobbered, so inputs lock.
  const [isRehydrating, setIsRehydrating] = React.useState(false);
  // Card order captured at first hydration and held for the session — saves
  // that change the ordering inputs must not shuffle the cards underfoot.
  const [frozenCardOrder, setFrozenCardOrder] = React.useState<readonly string[] | null>(null);

  // Seed editable state once the dependents are present and the residences query
  // has settled. Re-runs after a save that created/deleted residences (the save
  // re-requests hydration) so new-row ids are relinked and the delete snapshot
  // refreshes. Existing expand state is preserved across that re-hydration.
  React.useEffect(() => {
    if (!pendingHydration || dependents.length === 0 || residencesLoading) {
      return;
    }
    const seeded: Record<string, IDependentCardState> = {};
    rows.forEach((row) => {
      seeded[row.dependent.id] = seedCardState(row.dependent, residencesById.get(row.dependent.id) ?? null);
    });
    setCardStates(seeded);
    setExpanded((prev) => {
      const next = { ...prev };
      rows.forEach((row) => {
        if (!(row.dependent.id in next)) {
          next[row.dependent.id] = false;
        }
      });
      return next;
    });
    setLiveTogether(partnership?.liveTogether ?? null);
    // Settings always shows the frequent-locations list (no yes/no gate), so the
    // toggle is forced on — that's what `buildFrequentPlaceInputs` keys off.
    setFrequentLocations({ ...hydrateFrequentLocations(dependentResidences), toggle: true });

    const hydratedIds = new Set<string>();
    for (const { primary, other } of dependentResidences) {
      if (primary !== null && primary.ownerKind !== 'user') {
        hydratedIds.add(primary.id);
      }
      for (const r of other) {
        if (r.ownerKind !== 'user') {
          hydratedIds.add(r.id);
        }
      }
    }
    hydratedResidenceIdsRef.current = hydratedIds;

    baselineRef.current = seeded;
    liveTogetherBaselineRef.current = partnership?.liveTogether ?? null;
    frequentBaselineRef.current = { ...hydrateFrequentLocations(dependentResidences), toggle: true };
    hasEverHydratedRef.current = true;
    setPendingHydration(false);
    setIsRehydrating(false);
    // First hydration only — a post-save re-hydration keeps the captured order.
    setFrozenCardOrder((prev) => prev ?? rows.map((row) => row.dependent.id));
  }, [pendingHydration, dependents, residencesLoading, rows, residencesById, dependentResidences, partnership]);

  const handleCardChange = React.useCallback(
    (id: string) => (patch: Partial<IDependentCardState>) => {
      setCardStates((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
      // A refusal describes the value that was sent, so retyping retires it —
      // otherwise the stale message overrides the input's own format check.
      if (patch.email !== undefined) {
        setEmailErrors(withoutCard(id));
      }
      if (patch.phone !== undefined) {
        setPhoneErrors(withoutCard(id));
      }
    },
    []
  );

  const handleToggle = React.useCallback(
    (id: string) => () => setExpanded((prev) => ({ ...prev, [id]: !prev[id] })),
    []
  );

  const handleFrequentLocationsChange = React.useCallback((places: IFrequentPlace[]) => {
    setFrequentLocations((prev) => ({ ...prev, places }));
  }, []);

  const isHydrated = hasEverHydratedRef.current;

  // Every residence that should persist after a commit (each Main card's primary
  // + the shared frequent locations), changed or not. Drives the delete diff ONLY
  // — the upserts actually fired on commit are the changed subset (see `commit`),
  // so an unchanged residence is neither re-upserted nor deleted.
  const keptResidenceInputs = React.useMemo<UpsertResidenceInput[]>(() => {
    const ups: UpsertResidenceInput[] = [];
    rows.forEach((row) => {
      const state = cardStates[row.dependent.id];
      if (state && modeForRow(row, liveTogether) === 'main') {
        const [res] = buildResidenceInputs({ pathAForm: { dependentId: row.dependent.id, form: state.residence } });
        if (res) {
          ups.push(res);
        }
      }
    });
    ups.push(...buildFrequentPlaceInputs(frequentLocations));
    return ups;
  }, [rows, cardStates, liveTogether, frequentLocations]);

  const keptResidenceIds = React.useMemo(() => {
    const kept = new Set(keptResidenceInputs.map((r) => r.residenceId).filter((id): id is string => id != null));
    // A typed-over frequent row builds no upsert, but a commit triggered
    // elsewhere (a name blur, another pick) must not sweep it while the user is
    // still mid-edit — only a replacement pick, a removal, or a cleared text
    // field lets the orphan diff delete it.
    for (const place of frequentLocations.places) {
      if (isMidEditFrequentPlace(place) && place.id !== null) {
        kept.add(place.id);
      }
    }
    return kept;
  }, [keptResidenceInputs, frequentLocations.places]);
  const orphanResidenceIds = React.useMemo(
    () => [...hydratedResidenceIdsRef.current].filter((id) => !keptResidenceIds.has(id)),
    [keptResidenceIds]
  );

  const allValid =
    rows.every((row) => {
      const state = cardStates[row.dependent.id];
      return (
        state && isCardValid(modeForRow(row, liveTogether), state, isValidDateOfBirth, row.dependent.isAccountBacked)
      );
    }) && frequentLocations.places.every((p) => p.address === null || isCompleteAddress(p.address));

  const liveTogetherChanged = liveTogether !== liveTogetherBaselineRef.current;
  const frequentChanged =
    JSON.stringify(buildFrequentPlaceInputs(frequentLocations)) !==
    JSON.stringify(buildFrequentPlaceInputs(frequentBaselineRef.current));

  const commit = async () => {
    // Mid-rehydrate the card state is about to be rebuilt from the server — a
    // diff against it could carry residence ids that no longer exist, so even a
    // coalesced re-run is intentionally discarded here. Inputs are locked for
    // that window (`isRehydrating`), so no user edit can be pending in it.
    if (pendingHydration || !allValid) {
      return;
    }

    const tasks: Promise<unknown>[] = [];
    // Which task index belongs to which card's email or phone write. `allSettled`
    // keeps the order it was given, so the index is what survives the flattening.
    const contactTasks = new Map<number, { cardId: string; field: 'email' | 'phone' }>();
    // Fire only the residences that actually changed: each card's diffed
    // residence (from buildCardInputs) + the shared frequent list when it
    // changed. The delete diff below still uses the full keep-set, so an
    // unchanged residence is neither re-upserted nor deleted.
    const firedResidences: UpsertResidenceInput[] = [];
    for (const row of rows) {
      const state = cardStates[row.dependent.id];
      const baseline = baselineRef.current[row.dependent.id];
      if (!state || !baseline) {
        continue;
      }
      const inputs = buildCardInputs(row.dependent, modeForRow(row, liveTogether), state, baseline);
      if (inputs.dependent) {
        tasks.push(updateDependent({ variables: { input: inputs.dependent } }));
      }
      if (inputs.relationship) {
        tasks.push(updateCaregiverRelationship({ variables: { input: inputs.relationship } }));
      }
      if (inputs.contact) {
        tasks.push(updateDependentContact({ variables: { input: inputs.contact } }));
      }
      if (inputs.email) {
        contactTasks.set(tasks.length, { cardId: row.dependent.id, field: 'email' });
        tasks.push(updateDependentEmail({ variables: { input: inputs.email } }));
      }
      if (inputs.phone) {
        contactTasks.set(tasks.length, { cardId: row.dependent.id, field: 'phone' });
        tasks.push(updateDependentPhoneNumber({ variables: { input: inputs.phone } }));
      }
      if (inputs.residence) {
        firedResidences.push(inputs.residence);
      }
    }
    if (frequentChanged) {
      firedResidences.push(...buildFrequentPlaceInputs(frequentLocations));
    }
    for (const input of firedResidences) {
      tasks.push(upsertResidence({ variables: { input } }));
    }
    for (const residenceId of orphanResidenceIds) {
      tasks.push(deleteResidence({ variables: { input: { residenceId } } }));
    }
    if (liveTogetherChanged && partnership !== null && liveTogether !== null) {
      tasks.push(
        updateDependentPartnership({
          variables: { input: { dependantPartnershipId: partnership.id, liveTogether } },
        })
      );
    }

    if (tasks.length === 0) {
      return;
    }
    setSaveError(null);
    setEmailErrors({});
    setPhoneErrors({});

    const results = await Promise.allSettled(tasks);
    const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (failures.length > 0) {
      failures.forEach((failure) => logger.error('Failed to save dependent settings', failure.reason));
      const refusedEmails: Record<string, string> = {};
      const refusedPhones: Record<string, string> = {};
      let hasOtherFailure = false;
      results.forEach((result, index) => {
        if (result.status !== 'rejected') {
          return;
        }
        const task = contactTasks.get(index);
        if (task === undefined) {
          hasOtherFailure = true;
        } else if (task.field === 'email') {
          refusedEmails[task.cardId] = emailRefusalMessage(result.reason);
        } else {
          refusedPhones[task.cardId] = phoneRefusalMessage(result.reason);
        }
      });
      setEmailErrors(refusedEmails);
      setPhoneErrors(refusedPhones);
      // The banner speaks for everything that has no field of its own. A contact
      // refusal already reports itself under the field it names, so adding the
      // banner on top would tell the caregiver twice and say less the second time.
      if (hasOtherFailure) {
        setSaveError("Couldn't save your changes. Please try again.");
      }
      return;
    }

    // A residence create (null id → new row) or delete changes server-derived
    // references; refetch and re-hydrate so new ids relink and the delete
    // snapshot refreshes. Inputs lock for that window only — a fields-only save
    // adopts baselines in place and never disables anything. The adopted
    // baseline is this commit's snapshot, so edits made while it was in flight
    // diff as dirty and flush on the auto-commit re-run.
    const needsRefetch = orphanResidenceIds.length > 0 || firedResidences.some((r) => r.residenceId == null);
    if (needsRefetch) {
      setIsRehydrating(true);
      try {
        await refetchResidences();
      } catch (error) {
        // Mutations already landed; re-seed from whatever the cache holds
        // rather than leaving the form locked.
        logger.error('Failed to refresh residences after save', error);
      } finally {
        setPendingHydration(true);
      }
    } else {
      baselineRef.current = { ...cardStates };
      liveTogetherBaselineRef.current = liveTogether;
      frequentBaselineRef.current = frequentLocations;
    }
  };

  const requestCommit = useAutoCommit(commit);

  const handleLiveTogetherChange = React.useCallback(
    (value: boolean) => {
      setLiveTogether(value);
      // Marking the pair as living together sweeps the partner's now-contradictory
      // living situation: clear their lives-with-caregiver answer (the diff
      // persists it as `false`) — their own residence rows are already swept by
      // the household orphan-delete diff once the card is Referential.
      if (value) {
        const partner = rows.find((row) => row.isPartnerCard);
        if (partner) {
          setCardStates((prev) => {
            const state = prev[partner.dependent.id];
            if (!state || state.residence.liveWithCaregiver !== true) {
              return prev;
            }
            return {
              ...prev,
              [partner.dependent.id]: { ...state, residence: { ...state.residence, liveWithCaregiver: false } },
            };
          });
        }
      }
      requestCommit();
    },
    [rows, requestCommit]
  );

  if ((dependentsLoading || residencesLoading) && !isHydrated) {
    return <SettingsTabLoading />;
  }
  if (dependents.length === 0) {
    return <div className="text-secondary">No loved ones added yet.</div>;
  }

  // Only the rendered list is order-pinned — the commit/keep-set fan-outs above
  // iterate `rows` and are order-independent.
  const cards: IDependentCardProps[] = applyFrozenOrder(rows, frozenCardOrder)
    .filter((row) => cardStates[row.dependent.id])
    .map((row) => {
      const id = row.dependent.id;
      const state = cardStates[id];
      return {
        dependentId: id,
        state,
        onChange: handleCardChange(id),
        onCommit: requestCommit,
        isExpanded: Boolean(expanded[id]),
        onToggle: handleToggle(id),
        mode: modeForRow(row, liveTogether),
        isPartnerCard: row.isPartnerCard,
        partnerName: row.partnerName,
        liveTogether,
        onLiveTogetherChange: handleLiveTogetherChange,
        frequentLocations,
        onFrequentLocationsChange: handleFrequentLocationsChange,
        caregiverResidence: caregiverPrimaryResidence,
        apiKey,
        isDisabled: isRehydrating,
        isAccountBacked: row.dependent.isAccountBacked,
        // The saved address, not `state.email` — the verification the phone field
        // offers reads the stored row and refuses without one.
        hasEmailOnFile: Boolean(row.dependent.email?.trim()),
        isEmailChanged: hasEmailChanged(state, row.dependent),
        phoneNumberVerifiedAt: row.dependent.phoneNumberVerifiedAt,
        phoneVerificationStartedAt: row.dependent.phoneVerificationStartedAt,
        isPhoneChanged: hasPhoneChanged(state, row.dependent),
        onStartVerification: canStartVerification
          ? () => {
              setVerifyError(null);
              setVerifyingDependentId(id);
            }
          : undefined,
        isStartingVerification: isStartingVerification && verifyingDependentId === id,
        // Errors show live — with no save button, an invalid field is the only
        // visible reason edits have stopped persisting. The living-situation
        // unit (address + type cascade) saves atomically, so each missing piece
        // flags what it's holding up.
        errors: {
          firstName: !state.firstName.trim() ? 'First name is required' : undefined,
          lastName: !state.lastName.trim() ? 'Last name is required' : undefined,
          dateOfBirth:
            state.dateOfBirth !== null && !isValidDateOfBirth(state.dateOfBirth)
              ? 'Please enter a valid date of birth'
              : undefined,
          email: emailFieldError(state.email, row.dependent.isAccountBacked) ?? emailErrors[id],
          phone: phoneErrors[id],
          ...residenceFieldErrors(modeForRow(row, liveTogether), state.residence),
        },
      };
    });

  // Read off the card rather than the row: the number the caregiver is being
  // asked to check has to be the one on screen, and an unsaved edit is exactly
  // the case where those differ.
  const verifyingCard = cards.find((card) => card.dependentId === verifyingDependentId) ?? null;
  const verification: IPhoneVerificationPrompt | null = verifyingCard
    ? {
        name:
          displayName({ firstName: verifyingCard.state.firstName, preferredName: verifyingCard.state.preferredName }) ||
          'your loved one',
        phoneNumber: verifyingCard.state.phone,
        isStarting: isStartingVerification,
        error: verifyError ?? undefined,
        onClose: () => setVerifyingDependentId(null),
        onConfirm: () => {
          setVerifyError(null);
          startPhoneVerification({ variables: { input: { dependentId: verifyingCard.dependentId } } })
            .then(() => setVerifyingDependentId(null))
            // Every refusal is its own code with copy already written for a
            // caregiver, so the server's message is shown rather than replaced.
            .catch((error: unknown) =>
              setVerifyError(
                error instanceof Error && error.message ? error.message : "Couldn't start that. Please try again."
              )
            );
        },
      }
    : null;

  return <DependentSettingsFormBase cards={cards} saveError={saveError ?? undefined} verification={verification} />;
};

DependentSettingsForm.displayName = 'DependentSettingsForm';

export default DependentSettingsForm;
